/**
 * Karlılık (Senaryo) — Phase 1
 * Parent-based, interactive profitability scenario for Amazon US only.
 * Reuses PriceLab's existing pricing engine (calculateFBAPrice / calculateFBMPrice).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Search, X, Package, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  apiGetProfitabilityParents,
  apiGetAllProfitConfigs,
  apiGetAllCategoryColumnDefaults,
  apiGetShippingRates,
  type ProfitabilityParent,
  type MarketplaceProfitConfig,
} from '../../services/api';
import type {
  ProductData,
  FBAProfitConfig,
  FBMProfitConfig,
  ShippingRateBracket,
} from '../../types/pricing';
import { calculateFBAPrice, calculateFBMPrice, formatPercent } from '../../utils/pricing';
import { formatCurrency } from '../../utils/currency';
import { logger } from '../../utils/logger';

const COUNTRY = 'US';
const CURRENCY = 'USD';

// Sensible fallback assumptions (used when config / category defaults are missing)
const DEFAULT_ASSUMPTIONS = {
  sellingFeePercent: 15,
  advertisingPercent: 12,
  fbmCostPercent: 2,
  refundLossPercent: 4,
  warehousePercent: 4,
  shippingPerDesi: 1,
};

const DEFAULT_PRICES = [199, 204.9, 209.9];

interface Assumptions {
  sellingFeePercent: number;
  advertisingPercent: number;
  fbmCostPercent: number;
  warehousePercent: number;
  productCost: number;
  shippingPerDesi: number;
  // carried internally for the engine (not directly edited as separate fields below)
  fbaFeePercent: number;
  fbaCostPercent: number;
  refundLossPercent: number;
}

// Category-defaults row column_defaults shape uses keys like sellingPercent / adsPercent.
function pickCategoryDefaults(
  rows: Array<Record<string, unknown>>,
  category: string | null,
): Partial<Assumptions> {
  if (!category) return {};
  // Prefer FBA row for US; fall back to any US row for the category.
  const usRows = rows.filter(
    (r) => String(r.country_code) === COUNTRY && String(r.category) === category,
  );
  if (usRows.length === 0) return {};
  const row = usRows.find((r) => String(r.fulfillment_type) === 'FBA') || usRows[0];
  const cd = (row.column_defaults || {}) as Record<string, number>;
  const num = (v: unknown): number | undefined =>
    v === undefined || v === null ? undefined : Number(v);
  return {
    sellingFeePercent: num(cd.sellingPercent),
    advertisingPercent: num(cd.adsPercent),
    fbmCostPercent: num(cd.fbmCostPercent),
    refundLossPercent: num(cd.refundPercent),
    fbaFeePercent: num(cd.fbaFeePercent),
    fbaCostPercent: num(cd.fbaCostPercent),
  };
}

function marginColor(margin: number): string {
  if (margin >= 20) return 'text-green-600';
  if (margin >= 12) return 'text-amber-600';
  return 'text-red-600';
}

function marginBg(margin: number): string {
  if (margin >= 20) return 'bg-green-50';
  if (margin >= 12) return 'bg-amber-50';
  return 'bg-red-50';
}

const ProfitabilityPage: React.FC = () => {
  const [parents, setParents] = useState<ProfitabilityParent[]>([]);
  const [usConfig, setUsConfig] = useState<MarketplaceProfitConfig | null>(null);
  const [categoryDefaultRows, setCategoryDefaultRows] = useState<Array<Record<string, unknown>>>([]);
  const [shippingBrackets, setShippingBrackets] = useState<ShippingRateBracket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedParent, setSelectedParent] = useState<ProfitabilityParent | null>(null);
  const [fulfillment, setFulfillment] = useState<'FBA' | 'FBM'>('FBA');

  const [prices, setPrices] = useState<number[]>(DEFAULT_PRICES);
  const [assumptions, setAssumptions] = useState<Assumptions>({
    sellingFeePercent: DEFAULT_ASSUMPTIONS.sellingFeePercent,
    advertisingPercent: DEFAULT_ASSUMPTIONS.advertisingPercent,
    fbmCostPercent: DEFAULT_ASSUMPTIONS.fbmCostPercent,
    warehousePercent: DEFAULT_ASSUMPTIONS.warehousePercent,
    productCost: 0,
    shippingPerDesi: DEFAULT_ASSUMPTIONS.shippingPerDesi,
    fbaFeePercent: 0,
    fbaCostPercent: 0,
    refundLossPercent: DEFAULT_ASSUMPTIONS.refundLossPercent,
  });

  // Initial load
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [parentList, configs, catDefaults] = await Promise.all([
          apiGetProfitabilityParents(),
          apiGetAllProfitConfigs().catch(() => [] as MarketplaceProfitConfig[]),
          apiGetAllCategoryColumnDefaults().catch(() => [] as Array<Record<string, unknown>>),
        ]);
        setParents(parentList);
        setCategoryDefaultRows(catDefaults as Array<Record<string, unknown>>);
        const us = configs.find((c) => c.marketplace_code === 'US') || null;
        setUsConfig(us);

        // FBM (US BOTH mode) needs shipping brackets from TR_US route.
        try {
          const route = await apiGetShippingRates('TR_US');
          const brackets = (route?.brackets as Array<{ desi: number; rate: number }>) || [];
          setShippingBrackets(
            brackets.map((b) => ({
              routeCode: 'TR_US',
              desi: Number(b.desi),
              rate: Number(b.rate),
              currency: String(route?.currency || CURRENCY),
            })),
          );
        } catch {
          setShippingBrackets([]);
        }
      } catch (e) {
        logger.error('Failed to load profitability data', e);
        setError(e instanceof Error ? e.message : 'Veri yüklenemedi');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // When a parent is selected, pre-fill assumptions from config + category defaults.
  useEffect(() => {
    if (!selectedParent) return;

    const catDefaults = pickCategoryDefaults(categoryDefaultRows, selectedParent.category);
    const fbaCfg = usConfig?.fba_config;

    setAssumptions({
      sellingFeePercent:
        catDefaults.sellingFeePercent ?? DEFAULT_ASSUMPTIONS.sellingFeePercent,
      advertisingPercent:
        catDefaults.advertisingPercent ?? DEFAULT_ASSUMPTIONS.advertisingPercent,
      fbmCostPercent: catDefaults.fbmCostPercent ?? DEFAULT_ASSUMPTIONS.fbmCostPercent,
      warehousePercent:
        (fbaCfg?.warehousePercent ?? DEFAULT_ASSUMPTIONS.warehousePercent),
      productCost: selectedParent.cost ?? 0,
      shippingPerDesi: fbaCfg?.shippingPerDesi ?? DEFAULT_ASSUMPTIONS.shippingPerDesi,
      fbaFeePercent: catDefaults.fbaFeePercent ?? 0,
      fbaCostPercent: catDefaults.fbaCostPercent ?? 0,
      refundLossPercent:
        catDefaults.refundLossPercent ?? DEFAULT_ASSUMPTIONS.refundLossPercent,
    });
    setPrices(DEFAULT_PRICES);
  }, [selectedParent, usConfig, categoryDefaultRows]);

  const filteredParents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parents.slice(0, 50);
    return parents.filter((p) => p.parent.toLowerCase().includes(q)).slice(0, 50);
  }, [parents, search]);

  // Build categoryDefaults map for the engine from editable inputs.
  const engineCategoryDefaults = useMemo<Record<string, number>>(
    () => ({
      sellingFeePercent: assumptions.sellingFeePercent,
      fbaFeePercent: assumptions.fbaFeePercent,
      fbaCostPercent: assumptions.fbaCostPercent,
      fbmCostPercent: assumptions.fbmCostPercent,
      refundLossPercent: assumptions.refundLossPercent,
      advertisingPercent: assumptions.advertisingPercent,
    }),
    [assumptions],
  );

  const size = selectedParent?.size ?? 0;

  const productData = useMemo<ProductData>(
    () => ({
      id: selectedParent?.parent || '',
      name: selectedParent?.parent || '',
      category: selectedParent?.category || '',
      baseCost: assumptions.productCost,
      size,
    }),
    [selectedParent, assumptions.productCost, size],
  );

  // Flat single-bracket override so FBM shipping = shippingPerDesi × size (consistent with FBA).
  const effectiveBrackets = useMemo<ShippingRateBracket[]>(() => {
    if (size > 0) {
      return [{ routeCode: 'TR_US', desi: size, rate: assumptions.shippingPerDesi * size, currency: CURRENCY }];
    }
    return shippingBrackets;
  }, [size, assumptions.shippingPerDesi, shippingBrackets]);

  const results = useMemo(() => {
    if (!selectedParent) return [];
    return prices.map((price) => {
      if (fulfillment === 'FBA') {
        const fbaConfig: FBAProfitConfig = {
          shippingPerDesi: assumptions.shippingPerDesi,
          warehousePercent: assumptions.warehousePercent,
        };
        return calculateFBAPrice(price, productData, engineCategoryDefaults, fbaConfig, COUNTRY);
      }
      const fbmConfig: FBMProfitConfig = {
        shippingMode: 'LOCAL',
        fromTR: {
          customsDutyPercent: 0,
          ddpFee: 0,
        },
        fromLocal: { warehousePercent: assumptions.warehousePercent },
      };
      return calculateFBMPrice(price, productData, engineCategoryDefaults, fbmConfig, effectiveBrackets, COUNTRY);
    });
  }, [
    selectedParent,
    prices,
    fulfillment,
    assumptions.shippingPerDesi,
    assumptions.warehousePercent,
    productData,
    engineCategoryDefaults,
    effectiveBrackets,
  ]);

  const updatePrice = (idx: number, value: string) => {
    const next = [...prices];
    next[idx] = parseFloat(value) || 0;
    setPrices(next);
  };

  const updateAssumption = (key: keyof Assumptions, value: string) => {
    setAssumptions((prev) => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const numberInput = (
    label: string,
    value: number,
    onChange: (v: string) => void,
    suffix?: string,
    step = 0.1,
  ) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-7 h-7" />
          <div>
            <h2 className="text-2xl font-bold">Karlılık (Senaryo)</h2>
            <p className="opacity-90 text-sm">
              Parent bazlı interaktif karlılık senaryosu — Amazon US
            </p>
          </div>
        </div>
      </div>

      {/* Faz 5 (2026-08-14): bu modülün mantığı ManuMaestro Fiyat › Marj Tezgâhı'na taşındı.
          Orada iade ve reklam oranları seçilen tarih penceresinden ÖLÇÜLÜYOR; burada sabit
          varsayım. Sayfa referans olarak duruyor, güncellenmiyor. */}
      <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-4 text-sm">
        <b>Bu modül taşındı.</b> Parent bazlı kârlılık senaryosu artık{' '}
        <b>ManuMaestro › Fiyat › Marj Tezgâhı</b>'nda — orada iade ve reklam oranları seçtiğin
        tarih penceresinden gerçek veriyle ölçülüyor, ürün maliyeti de <code>cost_snapshots</code>'tan
        geliyor. Buradaki oranlar sabit varsayım olduğu için sonuçlar yanıltıcı olabilir;
        bu sayfa referans olarak duruyor ve <b>güncellenmiyor</b>.
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {/* Parent selector */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Ürün (Parent) Seç</h3>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Parent ara..."
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
            <RefreshCw className="w-4 h-4 animate-spin" /> Yükleniyor...
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
            {filteredParents.length === 0 ? (
              <div className="text-sm text-slate-400 p-4">Sonuç yok</div>
            ) : (
              filteredParents.map((p) => (
                <button
                  key={p.parent}
                  onClick={() => setSelectedParent(p)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
                    selectedParent?.parent === p.parent ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium text-slate-800 truncate">{p.parent}</span>
                    {p.mixed && (
                      <span title="Üyeler farklı maliyet/ölçüye sahip">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                    <span>{p.category || '—'}</span>
                    <span className="px-2 py-0.5 bg-slate-100 rounded-full">{p.productCount} ürün</span>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selectedParent && (
        <>
          {/* Selected product info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{selectedParent.parent}</h3>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                  <span>
                    Kategori: <span className="font-medium text-slate-800">{selectedParent.category || '—'}</span>
                  </span>
                  <span>
                    Maliyet:{' '}
                    <span className="font-medium text-slate-800">
                      {selectedParent.cost !== null ? formatCurrency(selectedParent.cost, CURRENCY) : '—'}
                    </span>
                  </span>
                  <span>
                    Desi:{' '}
                    <span className="font-medium text-slate-800">
                      {selectedParent.size !== null ? selectedParent.size : '—'}
                    </span>
                  </span>
                  <span>{selectedParent.productCount} ürün</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedParent(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                aria-label="Seçimi temizle"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {selectedParent.mixed && (
              <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2 text-xs">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Bu parent altındaki ürünler farklı maliyet/ölçüye sahip. Temsili değerler gösteriliyor.
              </div>
            )}
          </div>

          {/* Assumptions + scenario inputs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cost assumptions */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Maliyet Varsayımları</h3>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                  {(['FBA', 'FBM'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => setFulfillment(f)}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        fulfillment === f ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {numberInput('Satış Komisyonu %', assumptions.sellingFeePercent, (v) => updateAssumption('sellingFeePercent', v), '%')}
                {numberInput('Reklam %', assumptions.advertisingPercent, (v) => updateAssumption('advertisingPercent', v), '%')}
                {numberInput('FBM %', assumptions.fbmCostPercent, (v) => updateAssumption('fbmCostPercent', v), '%')}
                {numberInput('Depo Gideri %', assumptions.warehousePercent, (v) => updateAssumption('warehousePercent', v), '%')}
                {numberInput('Ürün Maliyeti', assumptions.productCost, (v) => updateAssumption('productCost', v), '$')}
                {numberInput('Kargo / Desi', assumptions.shippingPerDesi, (v) => updateAssumption('shippingPerDesi', v), '$')}
              </div>
            </div>

            {/* Price scenarios */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Fiyat Senaryoları</h3>
              <div className="space-y-3">
                {prices.map((price, idx) => (
                  <div key={idx}>
                    {numberInput(`Senaryo ${idx + 1}`, price, (v) => updatePrice(idx, v), '$', 0.1)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">Sonuçlar ({fulfillment})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">Fiyat</th>
                    <th className="text-right px-6 py-3 font-medium">Amazon Kesintileri</th>
                    <th className="text-right px-6 py-3 font-medium">Toplam Maliyet</th>
                    <th className="text-right px-6 py-3 font-medium">Net Kar</th>
                    <th className="text-right px-6 py-3 font-medium">Marj %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {results.map((r, idx) => (
                    <tr key={idx} className={marginBg(r.profitMargin)}>
                      <td className="px-6 py-3 font-medium text-slate-800">
                        {formatCurrency(prices[idx], CURRENCY)}
                      </td>
                      <td className="px-6 py-3 text-right text-slate-600">
                        {formatCurrency(r.totalAmazonCuts, CURRENCY)}
                      </td>
                      <td className="px-6 py-3 text-right text-slate-600">
                        {formatCurrency(r.totalCosts, CURRENCY)}
                      </td>
                      <td className={`px-6 py-3 text-right font-semibold ${marginColor(r.profitMargin)}`}>
                        {formatCurrency(r.netProfit, CURRENCY)}
                      </td>
                      <td className={`px-6 py-3 text-right font-bold ${marginColor(r.profitMargin)}`}>
                        {formatPercent(r.profitMargin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-slate-50 text-xs text-slate-400 border-t border-slate-100">
              Yeşil ≥ %20 · Sarı %12–20 · Kırmızı &lt; %12
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfitabilityPage;
